import { ChangeDetectionStrategy, Component, effect, inject, signal, untracked, WritableSignal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { LayerBloc } from '@connect-services/client/blocs/layer.bloc';
import { LayerEntityBloc } from '@connect-services/client/blocs/layer-entity.bloc';
import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { EntityDetailTemplateComponent } from '@connect-services/client/components/entity-detail-template/entity-detail-template.component';
import {
    AbstractEntityDetailComponent,
    AttributeValue,
    CenterEntityContext,
} from '@connect-services/client/components/table-entity-detail/abstract-entity-detail.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelSourceEnum,
    CenterEntityBroadcastChannelData,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { EntityLocation } from '@connect-services/client/services/map-draw.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { ProjectEntityTableService } from '@connect-services/client/services/project-entity-table.service';
import { ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { LayerAttribute } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EntityDetailTemplateComponent, LoadingComponent, RessourcesModule],
    selector: 'app-table-entity-detail',
    templateUrl: './table-entity-detail.component.html',
})
export class TableEntityDetailComponent extends AbstractEntityDetailComponent {
    protected readonly ButtonType = ButtonType;
    protected readonly geomBboxEntity: WritableSignal<EntityLocation | null> = signal(null);
    protected readonly layerBloc = inject(LayerBloc);
    protected readonly layerEntityBloc = inject(LayerEntityBloc);
    protected readonly layerListBloc = inject(LayerListBloc);
    protected readonly Object = Object;

    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly projectEntityTableService = inject(ProjectEntityTableService);

    public constructor() {
        super();
        effect(async () => {
            if (!this.layerEntityBloc.value() || !this.layerBloc.value()) {
                return;
            }

            this.editEntityDetailService.isEditMode();

            await untracked(async () => {
                this.isLoading.set(true);
                this.geomBboxEntity.set(null);

                await this.initLayerEntity();
                this.setIdentifier();
                this.isOpticalPathAvailable.set(this.layer()?.isUsedInOpticalPath ?? false);

                if (this.editEntityDetailService.isEditMode()) {
                    await this.editEntityDetailService.initEditForm(
                        this.bookmarkedAttributes(),
                        this.notBookmarkedAttributes(),
                    );
                }

                this.geomBboxEntity.set(this.getGeomBboxEntity());
                this.isLoading.set(false);
            });
        });
    }

    public centerMapOnEntity(): void {
        if (this.homeService.isExternalPage()) {
            const centerEntityData = this.getCenterEntityBroadcastData();
            if (!centerEntityData) {
                this.snackBarService.openErrorSnackBar('entityNotFoundOnMap');

                return;
            }

            this.centerExternalMapOnEntity(centerEntityData);

            return;
        }

        this.centerInternalMapOnEntity();
    }

    public getGeomBboxEntity(): EntityLocation | null {
        const layerEntity = this.layerEntityBloc.value();
        if (!layerEntity) {
            return null;
        }

        if (layerEntity.geom?.bbox && layerEntity.geom?.type) {
            return {
                bbox: layerEntity.geom!.bbox,
                type: layerEntity.geom!.type,
            };
        }

        return null;
    }

    public getLayerBookmarks(): Promise<LayerAttribute[]> {
        return firstValueFrom(
            this.layerService.layerControllerGetAttributes({
                id: this.layer()!.id,
            }),
        );
    }

    public async goToPreviousPage(): Promise<void> {
        const path = [RoutePathEnum.PROJECTS, this.projectBloc.value()?.id];

        await this.router.navigate(path, this.projectEntityTableService.setUrlQueryParams());
    }

    protected async initLayerEntity(): Promise<void> {
        this.layer.set(this.layerBloc.value());
        this.editEntityDetailService.layer.set(this.layerBloc.value());

        this.layerEntity.set(this.layerEntityBloc.value());
        this.editEntityDetailService.entityId.set(this.layerEntityBloc.value()!.uuid);

        this.editEntityDetailService.setBusinessIdentifierInfo();

        await this.setLayerDataAttributes();

        await this.setIsFromAStandardizedDeposit();
    }

    protected requestGeometryEditionFromTable(): void {
        const layer = this.layer();
        const layerEntity = this.layerEntity();
        const geomBboxEntity = this.geomBboxEntity();

        if (!layer || !layerEntity?.uuid || !layerEntity.geom || !geomBboxEntity) {
            this.snackBarService.openErrorSnackBar('entityNotFoundOnMap');

            return;
        }

        this.projectEditModeService.preserveEntityDetailPanel();

        this.mapDrawService.requestEditEntityGeometry({
            entityId: layerEntity.uuid,
            geom: layerEntity.geom,
            geomBboxEntity,
            layerId: layer.id,
        });

        if (this.contentLayoutService.isDataTableView()) {
            this.contentLayoutService.toggleIsDataView();
        }
    }

    protected async setLayerDataAttributes(): Promise<void> {
        const layerBookmark = await this.getLayerBookmarks();
        const bookmarks = new Map(layerBookmark.map((bookmark: LayerAttribute) => [bookmark.column, bookmark]));

        const [bookmarked, notBookmarked] = Object.entries(this.layerEntity()!).reduce<
            [AttributeValue[], AttributeValue[]]
        >(
            ([bookmarked, notBookmarked], [key, value]) => {
                const bookmark = bookmarks.get(key);

                if (this.isEntityExcluded(key, value)) {
                    return [bookmarked, notBookmarked];
                }

                const attributeValue: AttributeValue = {
                    key: key,
                    order: bookmark?.order,
                    value: value,
                };

                return bookmark?.isBookmarked
                    ? [[...bookmarked, attributeValue], notBookmarked]
                    : [bookmarked, [...notBookmarked, attributeValue]];
            },
            [[], []],
        );

        this.setAttributes(bookmarked, notBookmarked);
    }

    private centerExternalMapOnEntity(centerEntityData: CenterEntityBroadcastChannelData): void {
        const urlParams = this.routerService.getUrlParams();
        if (!urlParams.has(RoutePathEnum.EXT, 'true')) {
            return;
        }

        this.layersBroadcastChannel.postMessage(<BroadcastChannelEvent<CenterEntityBroadcastChannelData>>{
            additionalData: centerEntityData,
            message: BroadcastChannelMessageEnum.CENTER_ENTITY,
            source: BroadcastChannelSourceEnum.LAYER,
        });
    }

    private centerInternalMapOnEntity(): void {
        const context = this.getCenterEntityContext();
        if (!context) {
            this.snackBarService.openErrorSnackBar('entityNotFoundOnMap');

            return;
        }

        const { entityType } = this.mapDrawService.getSourceAndEntityType(context.layer.id, context.geom);

        this.mapDrawService.requestCenterEntity({
            entityId: context.layerEntity.uuid,
            entityType,
            geom: context.geom,
            geomBboxEntity: context.geomBboxEntity,
            layerId: context.layer.id,
        });

        if (this.projectEditModeService.isEditModeActive()) {
            this.projectEditModeService.preserveEntityDetailPanel();
        }

        if (this.contentLayoutService.isDataTableView()) {
            this.contentLayoutService.toggleIsDataView();
        }
    }

    private getCenterEntityBroadcastData(): CenterEntityBroadcastChannelData | null {
        const context = this.getCenterEntityContext();
        if (!context) {
            return null;
        }

        const { entityType, source } = this.mapDrawService.getSourceAndEntityType(context.layer.id, context.geom);

        if (!source) {
            return null;
        }

        return {
            entityId: context.layerEntity.uuid,
            entityType,
            geomBoxEntity: context.geomBboxEntity,
            source,
        };
    }

    private getCenterEntityContext(): CenterEntityContext | null {
        const layer = this.layer();
        const layerEntity = this.layerEntity();
        const geomBboxEntity = this.geomBboxEntity();

        if (!layer || !layerEntity?.geom || !geomBboxEntity) {
            return null;
        }

        return {
            geom: layerEntity.geom,
            geomBboxEntity,
            layer,
            layerEntity,
        };
    }

    private setIdentifier(): void {
        this.identifier = this.layerEntity()?.businessIdentifier
            ? this.layerEntity()!.businessIdentifier
            : this.layerEntity()!.uuid;
    }
}
